import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import { SIGN_BY_SLUG } from '../../data/astro/signs';
import { loadPair, matchFor, pairPath, pairSlug } from '../../data/compatibility';
import { Faq, ScoreBadge, ScoreBars, breadcrumbs, faqJsonLd } from './parts';
import '../Astro/Astro.css';
import './Compatibility.css';

const CompatibilityPair = ({ man, woman }) => {
  const slug = pairSlug(man, woman);
  const him = SIGN_BY_SLUG[man];
  const her = SIGN_BY_SLUG[woman];
  const match = matchFor(man, woman);
  const path = pairPath(man, woman);
  const [page, setPage] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setPage(null);
    setFailed(false);
    loadPair(slug)
      .then((data) => { if (live) setPage(data); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [slug]);

  const title = `${him.name} Man and ${her.name} Woman Compatibility`;
  const description = page?.meta_description || `${him.name} man and ${her.name} woman compatibility: ${match.overall}%. ${match.summary}`.slice(0, 155);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: title,
        description,
        url: `https://nowtarot.com${path}`,
        publisher: { '@type': 'Organization', name: 'NowTarot', url: 'https://nowtarot.com/' },
      },
      breadcrumbs([
        ['Zodiac compatibility', '/zodiac-compatibility/'],
        [`${him.name} compatibility`, `/zodiac-compatibility/${man}/`],
        [`${him.name} man and ${her.name} woman`, path],
      ]),
      ...(page?.faq ? [faqJsonLd(page.faq)] : []),
    ],
  };

  return (
    <div className="astro-page compat-page">
      <Helmet>
        <title>{`${title}: Love, Sex, and Marriage | NowTarot`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://nowtarot.com${path}`} />
        <meta property="og:title" content={`${title}: ${match.overall}%`} />
        <meta property="og:description" content={page?.intro || match.summary} />
        <meta property="og:url" content={`https://nowtarot.com${path}`} />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <nav className="compat-crumbs" aria-label="Breadcrumb">
          <Link to="/zodiac-compatibility/">Zodiac compatibility</Link> <span aria-hidden="true">/</span>{' '}
          <Link to={`/zodiac-compatibility/${man}/`}>{him.name}</Link> <span aria-hidden="true">/</span> {her.name} woman
        </nav>
        <p className="astro-eyebrow">{him.element} + {her.element} · {him.modality} + {her.modality}</p>
        <h1>
          <span className="compat-h1-glyph" aria-hidden="true">{him.glyph}</span> {him.name} man and{' '}
          <span className="compat-h1-glyph" aria-hidden="true">{her.glyph}</span> {her.name} woman <em>compatibility</em>
        </h1>

        <section className="astro-card compat-summary">
          <div className="compat-summary-head">
            <ScoreBadge score={match.overall} large />
            <div>
              <p className="astro-eyebrow">Overall match</p>
              <h2>{match.verdict}</h2>
            </div>
          </div>
          <p className="compat-intro">{page?.intro || match.summary}</p>
          <ScoreBars scores={match.scores} />
          <ShareRow
            url={path}
            label="Share with them"
            text={page?.share_line ? `${page.share_line} ${him.name} man + ${her.name} woman:` : `${him.name} man + ${her.name} woman, ${match.overall}% compatible:`}
            title={title}
          />
        </section>

        {page && (
          <>
            <nav className="compat-toc" aria-label="On this page">
              {page.sections.map((section) => <a key={section.id} href={`#${section.id}`}>{section.heading}</a>)}
            </nav>
            {page.sections.map((section) => (
              <section key={section.id} id={section.id} className="astro-section compat-reading">
                <h2>{section.heading}</h2>
                {section.paragraphs.map((paragraph) => <p key={paragraph.slice(0, 40)}>{paragraph}</p>)}
              </section>
            ))}
          </>
        )}
        {!page && !failed && <p className="astro-muted compat-loading">Loading the full reading…</p>}
        {failed && <p className="astro-error">The full reading didn’t load. Check your connection and refresh.</p>}

        {page?.celebrities?.length > 0 && (
          <section className="astro-section">
            <h2>Famous {him.name} and {her.name} couples</h2>
            <ul className="astro-tags">
              {page.celebrities.map((couple) => <li key={couple}>{couple}</li>)}
            </ul>
          </section>
        )}

        <section className="astro-card compat-deeper">
          <h2>Sun signs are only the start</h2>
          <p>
            Two people are more than two Sun signs. Compare both birth charts to see how your Venus, Moon, and Mars
            meet. It’s free, and you only need both birthdays.
          </p>
          <Link className="astro-button" to="/birth-chart-compatibility/">Compare our birth charts</Link>
        </section>

        <section className="astro-section">
          <h2>Keep reading</h2>
          <ul className="astro-next">
            {man !== woman && (
              <li>
                <Link to={pairPath(woman, man)}>
                  <strong>{her.name} man and {him.name} woman</strong>
                  <span>The same signs, the other way round.</span>
                </Link>
              </li>
            )}
            <li>
              <Link to={`/zodiac-compatibility/${man}/`}>
                <strong>{him.name} compatibility</strong>
                <span>Every sign ranked for {him.name}.</span>
              </Link>
            </li>
            {man !== woman && (
              <li>
                <Link to={`/zodiac-compatibility/${woman}/`}>
                  <strong>{her.name} compatibility</strong>
                  <span>Every sign ranked for {her.name}.</span>
                </Link>
              </li>
            )}
            <li>
              <Link to="/venus-sign-calculator/">
                <strong>Your Venus sign</strong>
                <span>How you love, and what wins you over.</span>
              </Link>
            </li>
          </ul>
        </section>

        {page?.faq && <Faq items={page.faq} title={`${him.name} man and ${her.name} woman FAQ`} />}
      </main>
      <Footer />
    </div>
  );
};

export default CompatibilityPair;
